import { motion } from "framer-motion";
import { EASE, Kinetic, Label, Reveal } from "@/components/motion";
import { CONTACT, useTitle } from "@/data/site";

const LEGAL = {
  privacy: {
    label: "Legal",
    title: "Privacy Policy",
    updated: "Last updated · August 2026",
    intro:
      "Urban Trail Pvt. Ltd. (“Urban Trail”, “we”, “us”) respects your privacy. This policy explains what information we collect through this website, why we collect it, and how it is handled.",
    sections: [
      {
        heading: "Information we collect",
        paragraphs: [
          "When you submit an enquiry through our website, we collect the details you choose to share with us: your name, email address, phone number, destination preferences, preferred travel dates, number of travellers, travel style, approximate budget and anything you tell us about your journey.",
          "We do not collect payment information on this website, and we do not ask for sensitive personal documents through our enquiry form.",
        ],
      },
      {
        heading: "How we use your information",
        paragraphs: [
          "We use the information you provide solely to respond to your enquiry, understand your travel preferences and plan journeys on your request. If you write to us, we may retain our correspondence so we can serve you better over time.",
          "We do not sell, rent or trade your personal information to anyone.",
        ],
      },
      {
        heading: "Communications",
        paragraphs: [
          "When you submit an enquiry, you will receive an email confirmation at the address you provided, followed by a personal response from our team. We may contact you by email, phone or WhatsApp using the details you shared, strictly in connection with your enquiry or journey.",
        ],
      },
      {
        heading: "Service providers",
        paragraphs: [
          "To operate this website and deliver our emails, we use trusted technology service providers, including website hosting and email delivery services. These providers process information only as needed to perform their services and are bound by their own privacy obligations.",
        ],
      },
      {
        heading: "Cookies and analytics",
        paragraphs: [
          "This website does not use advertising trackers. If we introduce analytics or cookies in future to improve the website experience, this policy will be updated to describe them.",
        ],
      },
      {
        heading: "Data retention and security",
        paragraphs: [
          "We retain enquiry information for as long as needed to assist with your travel planning and maintain our relationship with you. We apply reasonable technical and organisational measures to protect your information against unauthorised access.",
        ],
      },
      {
        heading: "Your choices",
        paragraphs: [
          `You may ask us at any time to access, correct or delete the personal information we hold about you, or to stop contacting you, by writing to ${CONTACT.email}.`,
        ],
      },
      {
        heading: "Contact",
        paragraphs: [
          `For any privacy questions, write to us at ${CONTACT.email} or call ${CONTACT.phone}. Our registered office is at ${CONTACT.office}.`,
        ],
      },
    ],
  },
  terms: {
    label: "Legal",
    title: "Terms & Conditions",
    updated: "Last updated · August 2026",
    intro:
      "These terms govern your use of the Urban Trail website. By using this website, you accept these terms. Please read them carefully.",
    sections: [
      {
        heading: "About this website",
        paragraphs: [
          "This website is operated by Urban Trail Pvt. Ltd., a travel company based in Kolkata, India. It presents our destinations, experiences and journal, and allows you to send us a travel enquiry.",
        ],
      },
      {
        heading: "Enquiries are not bookings",
        paragraphs: [
          "Submitting an enquiry through this website starts a conversation — it does not create a booking, a reservation or a contract for travel services. Journeys are confirmed only after a personalised proposal, your acceptance and the payment terms communicated to you separately in writing.",
        ],
      },
      {
        heading: "Cancellations & refunds",
        paragraphs: [
          "All cancellations and refunds for confirmed bookings are governed by our Cancellation Policy and Refund Policy, published on this website (linked in the footer) and attached to your invoice or voucher at the time of booking.",
        ],
      },
      {
        heading: "Accuracy of content",
        paragraphs: [
          "We take care to present destinations and experiences accurately, but travel conditions, availability and local circumstances change. Website content is for inspiration and general information; specific details for your journey are confirmed with you individually during planning.",
        ],
      },
      {
        heading: "Intellectual property",
        paragraphs: [
          "The Urban Trail name, logo, website design and written content are the property of Urban Trail Pvt. Ltd. and may not be reproduced without our written permission. Photography on this website is used under licence from its creators and stock photography providers.",
        ],
      },
      {
        heading: "Acceptable use",
        paragraphs: [
          "You agree not to misuse this website — including attempting to disrupt its operation, scraping its content at scale, or submitting false or misleading information through our forms.",
        ],
      },
      {
        heading: "Limitation of liability",
        paragraphs: [
          "This website is provided “as is”. To the extent permitted by law, Urban Trail Pvt. Ltd. is not liable for any indirect or consequential loss arising from use of this website. Travel services themselves are governed by the individual terms agreed with you at the time of booking.",
        ],
      },
      {
        heading: "Governing law",
        paragraphs: [
          "These terms are governed by the laws of India, and any disputes shall be subject to the jurisdiction of the courts of Kolkata, West Bengal.",
        ],
      },
      {
        heading: "Contact",
        paragraphs: [
          `Questions about these terms? Write to ${CONTACT.email}, call ${CONTACT.phone}, or visit us at ${CONTACT.office}.`,
        ],
      },
    ],
  },
  cancellation: {
    label: "Booking Policies",
    title: "Cancellation Policy",
    updated: "Effective · 06 January 2026",
    intro:
      "This policy applies to all bookings made with Urban Trail Pvt. Ltd. It explains how cancellations must be made, the charges that apply, and how approved refunds reach you.",
    sections: [
      {
        heading: "How to cancel",
        paragraphs: [
          `All cancellation requests must be submitted in writing via email to ${CONTACT.email}. Verbal cancellations — by phone or in person — will not be accepted or processed.`,
        ],
      },
      {
        heading: "Standard cancellation charges",
        paragraphs: [
          "The following charges are calculated on the total package cost, based on when your written cancellation reaches us before the date of travel:",
        ],
        list: [
          "30 days or more before travel — 30% of the total package cost (the non-refundable booking deposit is forfeited).",
          "15 to 29 days before travel — 50% of the total package cost.",
          "0 to 14 days before travel, or no-show — 100% of the total package cost (no refund).",
        ],
      },
      {
        heading: "Refund timeline",
        paragraphs: [
          "Approved refunds are processed within 10–15 working days, directly to the client's bank account. Cash refunds are not provided.",
        ],
      },
      {
        heading: "Peak season clause",
        paragraphs: [
          "For bookings during high-peak periods — for example Durga Puja, Diwali, Christmas/New Year and the May–June summer rush — 100% cancellation charges may apply from the date of booking, regardless of the timeline above. Where this applies, it will be clearly communicated at the time of booking.",
        ],
      },
      {
        heading: "Third-party losses",
        paragraphs: [
          "If a flight, train or specific hotel carries a stricter non-refundable policy than ours, that specific cost will be deducted in full before calculating any refundable amount.",
        ],
      },
    ],
  },
  refund: {
    label: "Booking Policies",
    title: "Refund Policy",
    updated: "Effective · January 2026",
    intro:
      "This policy explains when refunds apply, how they are calculated, and how they are paid for bookings made with Urban Trail Pvt. Ltd.",
    sections: [
      {
        heading: "General principle",
        paragraphs: [
          "Refunds are applicable only if a booking is cancelled within the valid cancellation window as per the specific product's policy (Domestic Holiday, International Holiday, Flight, or Visa). If a booking is non-refundable by nature — for example peak season bookings or deal fares — no refund will be processed.",
        ],
      },
      {
        heading: "Processing timeline",
        paragraphs: [
          "Approved refunds are typically processed within 10 to 20 working days from the date of cancellation confirmation. The time taken for the amount to reflect in your account depends on your bank's processing speed; we are not liable for delays caused by banking intermediaries.",
        ],
      },
      {
        heading: "Mode of refund",
        paragraphs: [
          "Refunds are generally processed to the original method of payment (credit card, debit card or bank account) used during the booking. Under no circumstances will refunds be issued in cash, or via personal wallets or UPI to a different number.",
        ],
      },
      {
        heading: "Deductions & non-refundable components",
        paragraphs: [
          "Before calculating the refundable amount, the following charges will be deducted:",
        ],
        list: [
          "Transaction fees — payment gateway charges, convenience fees and banking surcharges are 100% non-refundable.",
          "Service charges — Urban Trail Pvt. Ltd.'s professional service fees for planning and booking are non-refundable.",
          "Visa fees — visa application fees are non-refundable once paid to the Embassy.",
          "GST — Goods & Services Tax collected and deposited to the government is non-refundable.",
        ],
      },
      {
        heading: "Product-specific policies",
        list: [
          "Holiday packages (Domestic & International) — refunds are calculated based on the specific cancellation policy attached to your invoice or voucher. Third-party costs, such as non-refundable hotel rooms, are deducted before any payout.",
          "Flight tickets — refunds are subject to the airline's policy plus the Urban Trail Pvt. Ltd. service fee. If the airline declares a ticket “Non-Refundable”, only the tax component (if applicable by airline rule) may be refunded, minus our service charge.",
          "Visa services — visa fees and service charges are non-refundable if the application is rejected, delayed or withdrawn.",
        ],
      },
      {
        heading: "Force majeure",
        paragraphs: [
          "In the event of cancellations due to natural disasters, pandemics, political unrest or government restrictions, Urban Trail Pvt. Ltd. will adhere to the refund policies of the respective airlines, hotels and ground partners.",
          "If vendors offer a credit note or future travel voucher instead of a cash refund, we will pass the same benefit on to you. A cash refund will not be possible in such scenarios.",
        ],
      },
      {
        heading: "Jurisdiction",
        paragraphs: [
          "Any disputes regarding refunds shall be subject to the exclusive jurisdiction of the courts of Kolkata, West Bengal.",
        ],
      },
    ],
  },
};

export default function Legal({ kind }) {
  const doc = LEGAL[kind] || LEGAL.privacy;
  useTitle(`${doc.title} | Urban Trail`);

  return (
    <div data-testid={`legal-page-${kind}`}>
      <section className="relative flex min-h-[44vh] w-full items-end overflow-hidden bg-ink">
        <div className="mx-auto w-full max-w-[1600px] px-6 pb-14 pt-40 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          >
            <Label>{doc.label}</Label>
          </motion.div>
          <h1 className="mt-6 font-serif text-5xl font-light leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
            <Kinetic lines={[doc.title]} delay={0.3} />
          </h1>
          <motion.p
            className="label mt-8 text-white/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 0.8 }}
          >
            {doc.updated}
          </motion.p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-3xl px-6 py-20 lg:py-28">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-relaxed tracking-tight text-ink">
              {doc.intro}
            </p>
          </Reveal>
          <div className="mt-16 space-y-14">
            {doc.sections.map((s, i) => (
              <Reveal key={s.heading} delay={0.05}>
                <div data-testid={`legal-section-${i}`} className="border-t border-mist pt-10">
                  <h2 className="flex items-baseline gap-5 font-serif text-3xl font-light tracking-tight text-ink">
                    <span className="text-lg text-gold">{String(i + 1).padStart(2, "0")}</span>
                    {s.heading}
                  </h2>
                  {s.paragraphs && (
                    <div className="mt-6 space-y-5">
                      {s.paragraphs.map((p, j) => (
                        <p key={j} className="text-base leading-[1.85] text-graphite">
                          {p}
                        </p>
                      ))}
                    </div>
                  )}
                  {s.list && (
                    <ul className="mt-7 space-y-5">
                      {s.list.map((item, k) => (
                        <li key={k} className="flex gap-5 text-base leading-[1.85] text-graphite">
                          <span className="mt-[0.85em] block h-px w-6 shrink-0 bg-gold" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
